import React, { useCallback, useEffect, useState } from 'react';

const initialFilters = {
  search: '',
  mode: 'all',
  startDate: '',
  endDate: '',
  limit: 25,
  offset: 0
};

const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatCurrency = (cents) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
}).format(Number(cents || 0) / 100);

const formatDate = (value) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const getReference = (purchase) => (
  purchase.stripe_invoice_id
  || purchase.stripe_session_id
  || purchase.stripe_payment_intent
  || purchase.stripe_subscription_id
  || '—'
);

function CreditPurchaseHistory({ user }) {
  const [filters, setFilters] = useState(initialFilters);
  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPurchases = useCallback(async (signal) => {
    try {
      setLoading(true);
      const query = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== 'all') query.set(key, value);
      });

      const response = await fetch(`/api/auth/admin/credit-purchases?${query}`, {
        signal,
        headers: {
          Authorization: `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to fetch credit purchase history');

      setPurchases(data.purchases || []);
      setSummary(data.summary || null);
      setPagination(data.pagination || null);
      setError(null);
    } catch (requestError) {
      if (requestError.name !== 'AbortError') {
        console.error('Error fetching credit purchase history:', requestError);
        setError(requestError.message);
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [filters, user.token]);

  useEffect(() => {
    const controller = new AbortController();
    const delay = filters.search ? 250 : 0;
    const timer = setTimeout(() => fetchPurchases(controller.signal), delay);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [fetchPurchases, filters.search]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      offset: key === 'offset' ? value : 0
    }));
  };

  const resetFilters = () => setFilters(initialFilters);
  const hasFilters = filters.search || filters.mode !== 'all' || filters.startDate || filters.endDate;

  return (
    <div className="tab-content purchase-history-page">
      <div className="purchase-summary-grid">
        <div className="dashboard-card purchase-summary-card">
          <span className="purchase-summary-label">Purchases</span>
          <strong>{formatNumber(summary?.purchase_count)}</strong>
          <span>in the current results</span>
        </div>
        <div className="dashboard-card purchase-summary-card">
          <span className="purchase-summary-label">Gross revenue</span>
          <strong>{formatCurrency(summary?.gross_amount)}</strong>
          <span>recorded by Stripe</span>
        </div>
        <div className="dashboard-card purchase-summary-card">
          <span className="purchase-summary-label">Credits purchased</span>
          <strong>{formatNumber(summary?.credits_purchased)}</strong>
          <span>credits delivered</span>
        </div>
        <div className="dashboard-card purchase-summary-card">
          <span className="purchase-summary-label">Customers</span>
          <strong>{formatNumber(summary?.unique_customers)}</strong>
          <span>unique accounts</span>
        </div>
      </div>

      <div className="dashboard-card purchase-filters-card">
        <div className="card-header purchase-history-header">
          <div>
            <h2>Credit Purchase History</h2>
            <p>Completed Stripe payments that granted credits to customer accounts.</p>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => fetchPurchases()}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        <div className="purchase-filters">
          <div className="filter-group purchase-search">
            <label htmlFor="purchase-search">Search</label>
            <input
              id="purchase-search"
              type="search"
              className="filter-input"
              value={filters.search}
              onChange={(event) => updateFilter('search', event.target.value)}
              placeholder="Email, name, user ID, or Stripe ID"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="purchase-mode">Purchase type</label>
            <select
              id="purchase-mode"
              className="filter-select"
              value={filters.mode}
              onChange={(event) => updateFilter('mode', event.target.value)}
            >
              <option value="all">All types</option>
              <option value="payment">One-time</option>
              <option value="subscription">Subscription</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="purchase-start-date">From</label>
            <input
              id="purchase-start-date"
              type="date"
              className="filter-input"
              value={filters.startDate}
              onChange={(event) => updateFilter('startDate', event.target.value)}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="purchase-end-date">To</label>
            <input
              id="purchase-end-date"
              type="date"
              className="filter-input"
              value={filters.endDate}
              onChange={(event) => updateFilter('endDate', event.target.value)}
            />
          </div>
          {hasFilters && (
            <button type="button" className="btn btn-secondary btn-sm purchase-clear" onClick={resetFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="dashboard-card purchase-table-card">
        {error && (
          <div className="alert alert-error">
            <span>{error}</span>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => fetchPurchases()}>
              Retry
            </button>
          </div>
        )}

        {loading && purchases.length === 0 ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading purchase history…</p>
          </div>
        ) : purchases.length === 0 ? (
          <div className="empty-state">
            <p>No credit purchases found</p>
            <p className="empty-hint">Completed purchases will appear here after Stripe grants the credits.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="purchase-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Type</th>
                  <th>Credits</th>
                  <th>Amount</th>
                  <th>Balance after</th>
                  <th>Stripe reference</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((purchase) => {
                  const reference = getReference(purchase);
                  return (
                    <tr key={purchase.id}>
                      <td data-label="Date">{formatDate(purchase.created_at)}</td>
                      <td data-label="Customer">
                        <span className="purchase-customer-email">
                          {purchase.user_email || purchase.payer_email || 'Unknown customer'}
                        </span>
                        {purchase.payer_name && <span className="purchase-customer-name">{purchase.payer_name}</span>}
                        {purchase.user_id && <span className="purchase-user-id">User #{purchase.user_id}</span>}
                      </td>
                      <td data-label="Type">
                        <span className={`purchase-type-badge ${purchase.mode || 'unknown'}`}>
                          {purchase.mode === 'subscription' ? 'Subscription' : purchase.mode === 'payment' ? 'One-time' : 'Other'}
                        </span>
                        {purchase.billing_reason && <span className="purchase-billing-reason">{purchase.billing_reason.replaceAll('_', ' ')}</span>}
                      </td>
                      <td data-label="Credits" className="purchase-number">+{formatNumber(purchase.credits)}</td>
                      <td data-label="Amount" className="purchase-number">{formatCurrency(purchase.amount_total)}</td>
                      <td data-label="Balance after" className="purchase-number">{formatNumber(purchase.balance_after)}</td>
                      <td data-label="Stripe reference">
                        <code className="purchase-reference" title={reference}>{reference}</code>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {pagination?.total > 0 && (
          <div className="pagination modern">
            <div className="pagination-info">
              Showing <strong>{filters.offset + 1}</strong> to{' '}
              <strong>{Math.min(filters.offset + filters.limit, pagination.total)}</strong> of{' '}
              <strong>{formatNumber(pagination.total)}</strong> purchases
            </div>
            <div className="pagination-controls">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={filters.offset === 0 || loading}
                onClick={() => updateFilter('offset', Math.max(0, filters.offset - filters.limit))}
              >
                Previous
              </button>
              <span className="page-indicator">
                Page {Math.floor(filters.offset / filters.limit) + 1} of {Math.max(1, Math.ceil(pagination.total / filters.limit))}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={!pagination.hasMore || loading}
                onClick={() => updateFilter('offset', filters.offset + filters.limit)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreditPurchaseHistory;
